import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState, dispatch } from 'state/store'
import movementUSDALogo from 'assets/logos/movement-usda.svg'
import movementSavUSDLogo from 'assets/logos/movement-savusd.svg'
import { createSelector } from 'reselect'
import { TabProps } from 'components/common/Tab'
import { BORROW, COLLATERAL, DashTab, selectDashTab, setDashboardTab } from './dash'
import { FormattedPositions, MetaBroker } from 'state/mock'
import { doTx, TxReqPayload, tabToType, TxType } from 'state/thunks/doTx'
import { SignAndSubmitTransactionCallback } from 'state/thunks/doMoneyGun'

export const DEPOSIT_TAB = 'Supply'
export const WITHDRAW_TAB = 'Withdraw'
export const BORROW_TAB = 'Borrow'
export const REPAY_TAB = 'Repay'

export const COLLATERAL_TABS: FormTab[] = [DEPOSIT_TAB, WITHDRAW_TAB]
export const BORROW_TABS: FormTab[] = [BORROW_TAB, REPAY_TAB]

export type FormTab =
  | typeof DEPOSIT_TAB
  | typeof WITHDRAW_TAB
  | typeof BORROW_TAB
  | typeof REPAY_TAB

export const ALL_HISTORY = 'All activity'
export const USER_HISTORY = 'Your activity'

export type HistoryTab = typeof ALL_HISTORY | typeof USER_HISTORY

export const HISTORY_TABS: HistoryTab[] = [ALL_HISTORY, USER_HISTORY]
export interface FormState {
  formOpen: boolean
  activeBroker: MetaBroker
  formTab: FormTab
  inputVal: string
  txReview: boolean
  detailsOpen: boolean
  historyTab: HistoryTab
  multiplyFormOpen: boolean
  multiReviewOpen: boolean
  txAmount: number
  viewPortfolioModalOpen: boolean
}

export const initialState: FormState = {
  formOpen: false,
  activeBroker: null,
  formTab: DEPOSIT_TAB,
  inputVal: '',
  txReview: false,
  detailsOpen: false,
  historyTab: ALL_HISTORY,
  multiplyFormOpen: false,
  multiReviewOpen: false,
  txAmount: 0,
  viewPortfolioModalOpen: false
}

const slice = createSlice({
  name: 'form',
  initialState,
  reducers: {
    setFormOpen: (s) => {
      s.formOpen = true
    },
    setFormClosed: (s) => {
      s.formOpen = false
      s.inputVal = ''
      s.txReview = false
    },
    setActiveBroker: (s, a: PayloadAction<MetaBroker>) => {
      s.activeBroker = a.payload
    },
    setTab: (s, a: PayloadAction<FormTab>) => {
      s.formTab = a.payload
      s.inputVal = ''
    },
    setInputVal: (s, a: PayloadAction<string>) => {
      s.inputVal = a.payload
    },
    setTxReviewOpen: (s) => {
      s.txReview = true
    },
    setTxReviewClosed: (s) => {
      s.txReview = false
    },
    setMultiReviewOpen: (s) => {
      s.multiReviewOpen = true
    },
    setMultiReviewClosed: (s) => {
      s.multiReviewOpen = false
    },
    setDetailsOpen: (s) => {
      s.detailsOpen = true
    },
    setDetailsClosed: (s) => {
      s.detailsOpen = false
    },
    setHistoryTab: (s, a: PayloadAction<HistoryTab>) => {
      s.historyTab = a.payload
    },
    setMultiplyFormOpen: (s) => {
      s.multiplyFormOpen = true
    },
    setMultiplyFormClosed: (s) => {
      s.multiplyFormOpen = false
    },
    setTxAmountVal: (s, a: PayloadAction<number>) => {
      s.txAmount = a.payload
    },
    setViewPortfolioModalOpen: (s) => {
      s.viewPortfolioModalOpen = true
    },
    setViewPortfolioModalClosed: (s) => {
      s.viewPortfolioModalOpen = false
    }
  }
})

export default slice.reducer

//selectors
export const selectFormOpen = (s: RootState) => s.ui.form.formOpen
export const selectActiveBroker = (s: RootState) => s.ui.form.activeBroker
export const selectFormTab = (s: RootState) => s.ui.form.formTab
export const selectInputVal = (s: RootState) => s.ui.form.inputVal
export const selectTxReviewOpen = (s: RootState) => s.ui.form.txReview
export const selectDetailsOpen = (s: RootState) => s.ui.form.detailsOpen
export const selectHistoryTab = (s: RootState) => s.ui.form.historyTab
export const selectMultiplyFormOpen = (s: RootState) => s.ui.form.multiplyFormOpen
export const selectMultiReviewOpen = (s: RootState) => s.ui.form.multiReviewOpen
export const selectTxAmount = (s: RootState) => s.ui.form.txAmount
export const selectViewPortfolioModalOpen = (s: RootState) => s.ui.form.viewPortfolioModalOpen

//dispatches
export const {
  setFormOpen,
  setFormClosed,
  setActiveBroker,
  setTab,
  setInputVal,
  setTxReviewOpen,
  setTxReviewClosed,
  setDetailsOpen,
  setDetailsClosed,
  setHistoryTab,
  setMultiplyFormOpen,
  setMultiplyFormClosed,
  setMultiReviewOpen,
  setMultiReviewClosed,
  setTxAmountVal,
  setViewPortfolioModalOpen,
  setViewPortfolioModalClosed
} = slice.actions
export const openForm = () => {
  dispatch(setFormOpen())
}
export const closeForm = () => {
  dispatch(setFormClosed())
}
export const openDetails = () => {
  dispatch(setDetailsOpen())
}
export const closeDetails = () => {
  dispatch(setDetailsClosed())
}
export const setFormBroker = (b: MetaBroker) => {
  dispatch(setActiveBroker(b))
}
export const setFormTab = (t: FormTab) => {
  dispatch(setTab(t))
}
export const openTxReview = () => {
  dispatch(setTxReviewOpen())
}
export const closeTxReview = () => {
  dispatch(setTxReviewClosed())
}
export const setTabHistory = (t: HistoryTab) => {
  dispatch(setHistoryTab(t))
}
export const openMultiplyForm = () => {
  dispatch(setMultiplyFormOpen())
}
export const closeMultiplyForm = () => {
  dispatch(setMultiplyFormClosed())
}
export const openMultiReview = () => {
  dispatch(setMultiReviewOpen())
}
export const closeMultiReview = () => {
  dispatch(setMultiReviewClosed())
}
export const setTxAmount = (val: number) => {
  dispatch(setTxAmountVal(val))
}
export const openViewPortfolioModal = () => {
  dispatch(setViewPortfolioModalOpen())
}
export const closeViewPortfolioModal = () => {
  dispatch(setViewPortfolioModalClosed())
}

export interface IncentiveData {
  incentiveTicker: string
  supplyRewardRate: number
  borrowRewardRate: number
  stakeYieldRate: number
  stakeYieldTicker: string
  stakeYieldIcon: string
  stakeYieldPlatform: string
}

export const openFormWithData = (b: MetaBroker, tab?: FormTab, dashTab?: DashTab) => {
  if (dashTab) {
    setDashboardTab(dashTab)
  }
  setFormBroker(b)
  if (tab) {
    setFormTab(tab)
  }
  openForm()
}

export const openDetailsWithBroker = (b: MetaBroker) => {
  setFormBroker(b)
  openDetails()
}

export const setInput = (val: string) => {
  dispatch(setInputVal(val))
}

//prebuilds
export function switchFormLabels(tab: string) {
  switch (tab) {
    case COLLATERAL:
      return COLLATERAL_TABS
    case BORROW:
      return BORROW_TABS
  }
}
export const selectFormLabelsByDashTab = createSelector([selectDashTab], (tab) => {
  return switchFormLabels(tab)
})

export const selectBuiltFormTabs = createSelector(
  [selectFormTab, selectFormLabelsByDashTab],
  (selectedTab, labels) => {
    const tabs = buildFormTabs(labels, selectedTab)
    return tabs
  }
)

export const buildFormTabs = (labels: FormTab[], selected: FormTab) => {
  const dashTabs: TabProps[] = labels.map((label) => ({
    label,
    active: label === selected,
    onClick: () => dispatch(setTab(label))
  }))
  return dashTabs
}

export const buildHistoryTabs = (selected: HistoryTab) => {
  const historyTabs: TabProps[] = HISTORY_TABS.map((label) => ({
    label,
    active: label === selected,
    onClick: () => setTabHistory(label)
  }))
  return historyTabs
}

export const selectBuiltHistoryTabs = createSelector([selectHistoryTab], (selectedTab) => {
  return buildHistoryTabs(selectedTab)
})

export function formAction(
  tab: FormTab,
  broker: MetaBroker,
  txAmount: number,
  inputAmount: number,
  address: string,
  positions: FormattedPositions,
  signAndSubmitTransaction: SignAndSubmitTransactionCallback,
  brokerNames: string[]
) {
  const txType: TxType = tabToType[tab]
  const payload: TxReqPayload = {
    txType,
    txAmount,
    inputAmount,
    broker,
    address,
    positions,
    signAndSubmitTransaction,
    brokerNames
  }

  dispatch(doTx(txType)(payload))
}

export function calcNextFreeEquityVal(tab: FormTab, freeEquityVal: number, inputDollars: number) {
  if (tab === DEPOSIT_TAB || tab === REPAY_TAB) {
    return freeEquityVal + inputDollars
  } else {
    return freeEquityVal - inputDollars
  }
}

// The denomination the incentive
function getIncentiveTicker(assetName: string) {
  const incentiveTickers = {
    'movement-usdc': 'MOVE',
    'movement-usdt': 'MOVE',
    'movement-weth': 'MOVE',
    'movement-wbtc': 'MOVE',
    'movement-move-fa': 'MOVE',
    'movement-ezeth': 'MOVE',
    'movement-rseth': 'MOVE',
    'movement-weeth': 'MOVE'
  }
  return incentiveTickers[assetName] || ''
}

function getDailySupplyRewardInApt(assetName: string) {
  const rewardsPerAsset = {
    'movement-usdc': 0,
    'movement-usdt': 0,
    'movement-weth': 0,
    'movement-wbtc': 0,
    'movement-move-fa': 2375.32,
    'movement-ezeth': 0,
    'movement-rseth': 0,
    'movement-weeth': 0,
    'movement-susda': 0
  }
  const rewardApt = rewardsPerAsset[assetName] || 0
  return rewardApt
}

function getDailyBorrowRewardInApt(assetName: string) {
  const rewardsPerAsset = {
    'movement-usdc': 0,
    'movement-usdt': 8000,
    'movement-usdcx': 16000,
    'movement-weth': 0,
    'movement-wbtc': 0,
    'movement-move-fa': 0,
    'movement-ezeth': 0,
    'movement-rseth': 0,
    'movement-weeth': 0
  }
  const rewardApt = rewardsPerAsset[assetName] || 0
  return rewardApt
}

function getStakeYieldRate(assetName: string) {
  const stakeYieldRates = {
    'movement-susda': 0.05,
    'movement-savusd': 0.0753
  }
  return stakeYieldRates[assetName] || 0
}

function getStakeYieldTicker(assetName: string) {
  const stakeYieldTickers = {
    'movement-susda': 'USDa',
    'movement-savusd': 'avUSD'
  }
  return stakeYieldTickers[assetName] || ''
}

function getStakeYieldIcon(assetName: string) {
  const stakeYieldIcons = {
    'movement-susda': movementUSDALogo,
    'movement-savusd': movementSavUSDLogo
  }
  return stakeYieldIcons[assetName] || ''
}

function getStakeYieldPlatform(assetName: string) {
  const stakeYieldPlatforms = {
    'movement-susda': 'Avalon',
    'movement-savusd': 'Avant'
  }
  return stakeYieldPlatforms[assetName] || 'Avalon'
}

export function buildIncentiveData(b: MetaBroker, aptPrice: number): IncentiveData {
  if (!b || !aptPrice)
    return {
      incentiveTicker: '',
      supplyRewardRate: 0,
      borrowRewardRate: 0,
      stakeYieldRate: 0,
      stakeYieldTicker: '',
      stakeYieldIcon: '',
      stakeYieldPlatform: ''
    }
  const assetName = b.underlyingAsset.name
  const DAILY_SUPPLY_REWARD_IN_APT = getDailySupplyRewardInApt(assetName)
  const DAILY_SUPPLY_REWARD_IN_USD = DAILY_SUPPLY_REWARD_IN_APT * aptPrice
  const brokerSupply = b.scaledAvailableLiquidityUnderlying + b.scaledTotalBorrowedUnderlying
  const brokerSupplyValue = brokerSupply * b.underlyingAsset.price
  const dailySupplyRewardRate = DAILY_SUPPLY_REWARD_IN_USD / brokerSupplyValue
  const annualSupplyRewardRate = dailySupplyRewardRate * 365

  const DAILY_BORROW_REWARD_IN_APT = getDailyBorrowRewardInApt(assetName)
  const DAILY_BORROW_REWARD_IN_USD = DAILY_BORROW_REWARD_IN_APT * aptPrice
  const brokerBorrowValue =
    brokerSupplyValue - b?.scaledAvailableLiquidityUnderlying * b?.underlyingAsset.price
  const dailyBorrowRewardRate = DAILY_BORROW_REWARD_IN_USD / brokerBorrowValue
  const annualBorrowRewardRate = dailyBorrowRewardRate * 365

  const incentiveTicker = getIncentiveTicker(assetName)
  const stakeYieldRate = getStakeYieldRate(assetName)
  const stakeYieldTicker = getStakeYieldTicker(assetName)
  const stakeYieldIcon = getStakeYieldIcon(assetName)
  const stakeYieldPlatform = getStakeYieldPlatform(assetName)
  const incentiveData: IncentiveData = {
    incentiveTicker,
    supplyRewardRate: annualSupplyRewardRate,
    borrowRewardRate: annualBorrowRewardRate,
    stakeYieldRate,
    stakeYieldTicker,
    stakeYieldIcon,
    stakeYieldPlatform
  }
  return incentiveData
}
